<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useKleosStore } from '@/shared/stores/kleosStore'
  import {
    type CustomerUser,
    KleosOauthService,
    KleosService,
    type KleosSettings,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { yup } from '@/lib/validation'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { ROUTES } from '@/router/routes'

  const isLoading = ref(false)
  const isKleosAuthenticated = ref(false)

  const { fetchUser } = useUserStore()
  const user = computed<CustomerUser>(() => useUserStore().user)
  const kleosStore = useKleosStore()

  const isKleosEnabled = ref(false)
  const kleosClientId = ref('')
  const kleosClientSecret = ref('')
  const originalKleosClientSecret = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description: 'Configure Kleos integration settings for JUPUS.',
      enableKleos: 'Enable Kleos Integration',
      kleosClientId: 'Kleos Client ID',
      kleosClientIdPlaceholder: 'Enter Kleos Client ID',
      kleosSecret: 'Kleos Secret',
      kleosSecretPlaceholder: 'Enter Kleos Secret',
      invalidUuid: 'Please enter a valid UUID format (e.g., 72155790-00df-49e5-8cd6-7fa142430abc)',
      saving: 'Saving...',
      linkJupusWithKleos: 'Link Jupus with Kleos',
      disconnectKleos: 'Disconnect Kleos'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: 'Die Kleos Schnittstelle ',
      enableKleos: 'Kleos Integration aktivieren',
      kleosClientId: 'Kleos Client ID',
      kleosClientIdPlaceholder: 'Kleos Client ID eingeben',
      kleosSecret: 'Kleos Secret',
      kleosSecretPlaceholder: 'Enter Kleos Secret eingeben',
      invalidUuid:
        'Bitte geben Sie ein gültiges UUID-Format ein (z.B. 72155790-00df-49e5-8cd6-7fa142430abc)',
      saving: 'Speichern...',
      linkJupusWithKleos: 'Jupus mit Kleos verbinden',
      disconnectKleos: 'Kleos Verbindung trennen'
    }
  })

  const checkKleosAuthentication = async () => {
    try {
      await KleosService.kleosCaseTypesRetrieve()
      isKleosAuthenticated.value = true
      return true
    } catch (error) {
      isKleosAuthenticated.value = false
      return false
    }
  }

  const disconnectKleos = async () => {
    try {
      isLoading.value = true
      await KleosOauthService.kleosOauthLogoutCreate()
      isKleosAuthenticated.value = false
      // Refresh user data to update UI
      await fetchUser()
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      // Try/catch proposed by coderabbitai
      const kleosSettings: KleosSettings =
        (await SettingsService.settingsKleosList()) as KleosSettings

      // Initialize form with existing values if available
      if (kleosSettings) {
        isKleosEnabled.value = kleosSettings.is_kleos_integration_enabled || false
        kleosClientId.value = kleosSettings.kleos_client_id || ''
        formData.kleos_client_id = kleosClientId.value
        kleosClientSecret.value = kleosSettings.kleos_client_secret || ''
        originalKleosClientSecret.value = kleosSettings.kleos_client_secret || ''
        formData.kleos_client_secret = kleosClientSecret.value

        // Check if Kleos is authenticated
        await checkKleosAuthentication()
      }
    } catch (error) {
      console.error('Error loading Kleos settings:', error)
      Sentry.captureException(error)
    }
  })

  const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  // Validation schema
  const validationSchema = {
    kleos_client_id: yup
      .string()
      .transform((value) => (value === '' ? null : value)) // Transform empty string to null
      .nullable()
      .optional(), // Make field optional
    kleos_client_secret: yup
      .string()
      .transform((value) => (value === '' ? null : value)) // Transform empty string to null
      .matches(uuidv4Regex, t('invalidUuid'))
      .nullable()
      .optional() // Make field optional
  }

  interface FormData {
    kleos_client_id: string
    kleos_client_secret: string
  }

  const { formData, formErrors, touchedFields, validateField, handleFieldChange } =
    useFormWithValidation<FormData>(
      {
        kleos_client_id: '',
        kleos_client_secret: ''
      },
      validationSchema
    )

  const updateKleosSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: Record<string, any> = {
        is_kleos_integration_enabled: isKleosEnabled.value
      }

      // Only include Kleos Client ID if it has a value
      if (kleosClientId.value) {
        // Validate UUID format
        const isValid = await validateField('kleos_client_id', kleosClientId.value)
        if (isValid) {
          data.kleos_client_id = kleosClientId.value
        }
      }

      // Only include Kleos Client Secret if it has a value and is valid,
      // as well as is not the same as the original value anymore
      if (kleosClientSecret.value) {
        // Validate uuid format
        const isValid = await validateField('kleos_client_secret', kleosClientSecret.value)
        if (isValid && kleosClientSecret.value !== originalKleosClientSecret.value) {
          data.kleos_client_secret = kleosClientSecret.value
        }
      }

      await SettingsService.settingsKleosCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  // Add watchers for the input fields
  watch(isKleosEnabled, () => {
    if (user.value) {
      updateKleosSettings()
    }
  })

  watch(kleosClientId, (newValue) => {
    formData.kleos_client_id = newValue
    handleFieldChange('kleos_client_id', newValue)
  })

  watch(kleosClientSecret, (newValue) => {
    formData.kleos_client_secret = newValue
    handleFieldChange('kleos_client_secret', newValue)
  })

  const openKleosAuthPopup = async () => {
    await updateKleosSettings()
    const kleosAuthUrl = ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.KLEOS_OAUTH
    const width = 800
    const height = 700
    const left = (window.screen.width - width) / 2
    const top = (window.screen.height - height) / 2

    // Open the popup window with the Kleos authorization page
    const popup = window.open(
      kleosAuthUrl,
      'KleosAuthentication',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    )

    // Poll every 1 second to check if the kleos authentication was successful
    if (popup) {
      // reset authentication state once successful and refresh user data
      const checkClosed = setInterval(async () => {
        if (kleosStore.getAuthenticationState()) {
          clearInterval(checkClosed)
          // Refresh user data to update UI
          await fetchUser()
          await checkKleosAuthentication()
          kleosStore.resetAuthenticationState()
        }
      }, 1000)
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- Kleos Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable Kleos Integration -->
          <CheckboxField
            v-model="isKleosEnabled"
            name="is_kleos_integration_enabled"
            :label="t('enableKleos')"
          />
          <!-- Kleos settings (only shown if Kleos is enabled) -->
          <div v-if="isKleosEnabled">
            <InputField
              v-model="kleosClientId"
              name="kleos_client_id"
              :label="t('kleosClientId')"
              :placeholder="t('kleosClientIdPlaceholder')"
            />

            <InputField
              v-model="kleosClientSecret"
              name="kleosClientSecret"
              :label="t('kleosSecret')"
              :placeholder="t('kleosSecretPlaceholder')"
              :disabled="isKleosAuthenticated"
              type="password"
            />

            <button
              v-if="!isKleosAuthenticated"
              class="mt-5 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              :disabled="kleosClientId.length === 0 || kleosClientSecret.length === 0"
              @click="openKleosAuthPopup"
            >
              {{ t('linkJupusWithKleos') }}
            </button>
            <button
              v-else
              class="mt-5 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-red-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-red-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              @click="disconnectKleos"
            >
              {{ t('disconnectKleos') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
